import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import baseURL from '../component/baseURL';
import showNotification from '../component/Notification';

const labels = ['Not Shared', 'B to B', 'B to C'];

/** Must match ConnectionFeedSettings: API feed5 = Pocket, API feed6 = Feed 5 in UI. */
function apiFieldIndexForBlockId(blockId) {
    const n = Number(blockId);
    if (n === 5) return 6;
    if (n === 6) return 5;
    return n;
}

function toLabel(shareType) {
    if (Number(shareType) === 1) return 'B to B';
    if (Number(shareType) === 2) return 'B to C';
    return 'Not Shared';
}

export default function ConnectionFeedSettingsDetails({ hideMobileHeader = false }) {
    const location = useLocation();
    const navigate = useNavigate();
    const feedType = location.state?.feedType ?? 1;
    const apiFeedTypeForRequest =
        location.state?.apiFeedType ?? apiFieldIndexForBlockId(location.state?.feedType ?? 1);
    const listingIds = String(location.state?.listingIds ?? '').trim();
    const shareTypeFromState = Number(location.state?.shareType ?? 0);
    const shareType = listingIds === '' ? 2 : shareTypeFromState;
    const memberId = location.state?.memberId ?? '';
    const [isMobile, setIsMobile] = useState(false);
    const [loading, setLoading] = useState(true);
    const [memberDetails, setMemberDetails] = useState(location.state?.memberDetails ?? {});
    const [portfolioArray, setPortfolioArray] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(shareType);
    const [feedListingIds, setFeedListingIds] = useState(listingIds ? `${listingIds.replace(/,\s*$/, '')},` : '');
    const totalPublished = Number(memberDetails?.total_published ?? location.state?.totalPublished ?? 0) || 0;
    const totalAccepted = Number(memberDetails?.total_accepted ?? location.state?.totalAccepted ?? 0) || 0;
    const totalShared = Number(memberDetails?.total_shared ?? location.state?.totalShared ?? 0) || 0;
    const feedTitle = location.state?.feedTitle ?? (Number(feedType) === 6 ? 'Pocket Feed' : `Feed ${feedType}`);

    const fullName = useMemo(
        () => (memberDetails?.user_name || '').trim() || [memberDetails?.fname, memberDetails?.lname].filter(Boolean).join(' ') || 'Member',
        [memberDetails]
    );
    const memberLocation = useMemo(
        () => [memberDetails?.city_name, memberDetails?.country_name].filter(Boolean).join(' - ') || '—',
        [memberDetails]
    );
    const memberImage = memberDetails?.user_image_thumb || memberDetails?.user_image || 'https://placehold.co/80x80';
    const companyImage = memberDetails?.company_image_thumb || memberDetails?.company_image || 'https://placehold.co/80x80?text=Logo';
    const selectedTypeLabel = toLabel(selectedIndex);

    useEffect(() => {
        const userId = localStorage.getItem('user_id');
        if (!userId || !memberId) {
            setLoading(false);
            return;
        }
        fetchFeedDetails(userId, memberId, apiFeedTypeForRequest, selectedIndex);
    }, [memberId, feedType, selectedIndex, apiFeedTypeForRequest]);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 767);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    function fetchFeedDetails(userId, memberid, apiFeedType, sharetype) {
        setLoading(true);
        const formdata = new FormData();
        formdata.append('user_id', userId);
        formdata.append('member_id', memberid);
        formdata.append('feed_type', String(apiFeedType));
        formdata.append('share_type', String(sharetype));
        formdata.append('key', '81dc9bdb52d04dc20036dbd8313ed055');

        fetch(baseURL.URL + '/portfolio_feed/view_feed_by_type', {
            method: 'POST',
            body: formdata
        }).then((response) => response.json())
            .then((responseJson) => {
                if (responseJson.server?.code === 200) {
                    const result = responseJson.response?.result ?? {};
                    const pArray = Array.isArray(result.portfolio_array) ? result.portfolio_array : [];
                    setMemberDetails(result.member_details ?? {});
                    setPortfolioArray(pArray);
                } else {
                    showNotification('Error', responseJson.server?.message || 'Failed to load listings', 'danger');
                }
            })
            .catch((error) => {
                showNotification('Error', error.message || 'Failed to load listings', 'danger');
            })
            .finally(() => setLoading(false));
    }

    function handleIndexChange(index) {
        setSelectedIndex(index);
    }

    function toggleFunc(listingID) {
        const id = String(listingID ?? '').trim();
        if (!id) return;
        const token = `${id},`;
        setFeedListingIds((prev) => {
            let next = prev || '';
            if (next.includes(token)) {
                next = next.replace(token, '');
            } else {
                next = `${next}${token}`;
                if (selectedIndex === 0) setSelectedIndex(1);
            }
            return next;
        });
    }

    function isListingSelected(listingID) {
        const id = String(listingID ?? '').trim();
        if (!id) return false;
        return feedListingIds.includes(`${id},`);
    }

    function backButtonClick() {
        let stringifiedIds = feedListingIds || '';
        if (!(selectedIndex === 0 || stringifiedIds === '')) {
            stringifiedIds = stringifiedIds.replace(/,\s*$/, '');
        }
        const updatedFeed = {
            feedType: Number(feedType),
            shareType: selectedIndex,
            selectedType: toLabel(selectedIndex),
            listingIds: stringifiedIds,
        };
        const returnTo = location.state?.returnTo;
        const settingsState = returnTo?.state ?? {};
        navigate('/feed/connection-feed-settings', {
            replace: true,
            state: {
                ...settingsState,
                updatedFeed,
            },
        });
    }




    return (
        <React.Fragment>
            {!hideMobileHeader && isMobile && <div className="mobile-page-header incoming-member-feed-header flex align-items-center">
                <button type="button" className="mobile-back-btn" onClick={backButtonClick} aria-label="Back">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18L9 12L15 6" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <h2 className="mobile-page-title incoming-member-feed-title">{Number(feedType) === 6 ? 'POCKET FEED' : `FEED ${feedType}`}</h2>
            </div>}

            <div className="content-right connection-feed-settings-page connection-feed-details-page">
                <div className="connection-feed-settings-wrap">
                    <div className="connection-feed-member-card w-full cursor-pointer" onClick={() => {
                        navigate('/member-profile', {
                            replace: true,
                            state: {
                                memberID: memberDetails?.user_id,
                                member: memberDetails,
                                from: '/feed/connection-feed-settings-details',
                                returnTo: {
                                    path: '/feed/connection-feed-settings-details',
                                    state: { ...location.state },
                                },
                            },
                        });
                    }}>
                        <div className="connection-feed-member-left">
                            <img src={memberImage} alt={fullName} className="connection-feed-member-avatar" />
                            <div className="connection-feed-member-meta">
                                <h3>{fullName}</h3>
                                <p>{memberLocation}</p>
                            </div>
                        </div>
                        <img src={companyImage} alt="Company" className="connection-feed-company-logo" />
                    </div>

                    <div className="connection-feed-stat-box w-full">
                        <div className="connection-feed-stat-item">
                            <strong>{totalPublished}</strong>
                            <span>Total Published</span>
                        </div>
                        <div className="connection-feed-stat-divider" />
                        <div className="connection-feed-stat-item">
                            <strong>{totalAccepted}</strong>
                            <span>Total Accepted</span>
                        </div>
                        <div className="connection-feed-stat-divider" />
                        <div className="connection-feed-stat-item">
                            <strong>{totalShared}</strong>
                            <span>Total Shared</span>
                        </div>
                    </div>

                    <div className="connection-feed-tab-row">
                        {labels.map((label, index) => (
                            <span
                                key={label}
                                className={`connection-feed-tab ${selectedTypeLabel === label ? 'active' : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleIndexChange(index)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleIndexChange(index);
                                    }
                                }}
                            >
                                {label}
                            </span>
                        ))}
                    </div>

                    {loading ? (
                        <div className="page-loader-wrapper" style={{ padding: 16 }}>
                            <div className="react-js-loader">
                                <div className="react-js-loader-spinner"></div>
                                <p className="react-js-loader-text">Loading...</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {portfolioArray.length === 0 ? (
                                <div className="connection-feed-details-card">
                                    <p className="no-data-message">No listings found.</p>
                                </div>
                            ) : (
                                portfolioArray.map((listing, idx) => {
                                    const listingTitle = [listing.StreetNumber, listing.StreetName].filter(Boolean).join(' ').trim() || `Listing ${idx + 1}`;
                                    const listingLocation = [listing.city_name, listing.country_name].filter(Boolean).join(', ') || '—';
                                    const listingImage = listing.image || 'https://placehold.co/96x64';
                                    return (
                                        <div key={listing.listing_id || idx} className="connection-feed-details-card">
                                            <div className="connection-feed-details-top">
                                                <div className="connection-feed-listing-left">
                                                    <img src={listingImage} alt={listingTitle} className="connection-feed-listing-thumb" />
                                                    <div className="connection-feed-listing-meta">
                                                        <strong>{listingTitle}</strong>
                                                        <p>{listingLocation}</p>
                                                    </div>
                                                </div>
                                                <label className="connection-feed-details-toggle" aria-label="Subscribed">
                                                    <input
                                                        type="checkbox"
                                                        checked={isListingSelected(listing.listing_id)}
                                                        onChange={() => toggleFunc(listing.listing_id)}
                                                    />
                                                    <span />
                                                </label>
                                            </div>

                                            <div className="connection-feed-details-tags">
                                                <span>{feedTitle}</span>
                                                <span>{selectedTypeLabel}</span>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </>
                    )}
                </div>
            </div>
        </React.Fragment>
    );
}
